import { useState, useRef, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  Phone, Menu, X, ChevronDown, ArrowRight,
  Scale, Briefcase, Landmark, RotateCcw, GraduationCap,
  FileText, MessageSquareText, Newspaper, Gavel,
  Siren, MessageCircleQuestion, SearchCheck, CalendarClock, Users, PhoneCall,
  Instagram, Facebook, Linkedin,
} from "lucide-react";
import { FIRM } from "@/data/content";
import { SERVICE_GROUPS } from "@/data/servicesIA";
import { SITUATIONS } from "@/data/situations";
import { CATEGORIES, LATEST } from "@/data/resources";

const ICONS = {
  Scale, Briefcase, Landmark, RotateCcw, GraduationCap,
  FileText, MessageSquareText, Newspaper, Gavel,
  Siren, MessageCircleQuestion, SearchCheck, CalendarClock, Users, PhoneCall,
};

const columnGroups = SERVICE_GROUPS.filter((g) => g.children.length > 0);
const standaloneGroups = SERVICE_GROUPS.filter((g) => g.children.length === 0);

function Panel({ children, testId, onEnter, onLeave }) {
  return (
    <div
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      className="absolute inset-x-0 top-full mt-2 hidden animate-[megaIn_.18s_ease-out] border border-slate-200 bg-white p-6 shadow-2xl shadow-black/20 lg:block"
      data-testid={testId}
    >
      {children}
    </div>
  );
}

export default function Navbar() {
  const [openMenu, setOpenMenu] = useState(null); // 'services' | 'help' | 'resources'
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const closeTimer = useRef(null);
  const location = useLocation();

  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
    setMobileSection(null);
  }, [location.pathname]);

  useEffect(() => {
    const updateScrolled = () => setIsScrolled(window.scrollY > 24);
    updateScrolled();
    window.addEventListener("scroll", updateScrolled, { passive: true });
    return () => window.removeEventListener("scroll", updateScrolled);
  }, []);

  const open = (menu) => {
    clearTimeout(closeTimer.current);
    setOpenMenu(menu);
  };
  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenMenu(null), 150);
  };

  const navLinkCls = ({ isActive }) =>
    `${isScrolled ? "px-2.5 py-1.5" : "px-3.5 py-2"} text-sm tracking-wide transition-colors duration-200 ${
      isActive ? "text-brand-gold" : "text-slate-200 hover:text-white"
    }`;

  const triggerCls = (menu) =>
    `flex items-center gap-1 ${isScrolled ? "px-2.5 py-1.5" : "px-3.5 py-2"} text-sm tracking-wide transition-colors duration-200 ${
      openMenu === menu ? "text-brand-gold" : "text-slate-200 hover:text-white"
    }`;

  const Trigger = ({ menu, label, testId }) => (
    <div onMouseEnter={() => open(menu)} onMouseLeave={scheduleClose}>
      <button
        onClick={() => setOpenMenu(openMenu === menu ? null : menu)}
        className={triggerCls(menu)}
        data-testid={testId}
      >
        {label}
        <ChevronDown className={`size-3.5 transition-transform duration-200 ${openMenu === menu ? "rotate-180" : ""}`} />
      </button>
    </div>
  );

  return (
    <header className="fixed inset-x-0 top-0 z-50 bg-brand-navy/95 shadow-2xl shadow-black/30 backdrop-blur-xl transition-all duration-300" data-testid="site-header">
      <div className={`relative mx-auto max-w-7xl transition-all duration-300 ${isScrolled ? "px-3 sm:px-4 lg:px-6" : "px-4 sm:px-6 lg:px-8"}`}>
        {/* Unified header block: eyebrow + main bar */}
        <div>
        <div
          className={`flex items-center justify-end overflow-hidden transition-all duration-300 ${isScrolled ? "h-0 opacity-0" : "h-9 opacity-100"}`}
          data-testid="eyebrow-bar"
        >
          <div className="flex items-center gap-4">
            <a
              href={`tel:${FIRM.phone.replace(/\s/g, "")}`}
              className="flex items-center gap-1.5 text-[11px] text-slate-300 transition-colors hover:text-brand-gold"
              data-testid="eyebrow-office-phone"
            >
              <Phone className="size-3 text-brand-gold" />
              {FIRM.phone}
            </a>
            <a
              href={`tel:${FIRM.emergencyPhone.replace(/\s/g, "")}`}
              className="hidden items-center gap-1.5 text-[11px] text-slate-300 transition-colors hover:text-brand-gold md:flex"
              data-testid="eyebrow-emergency-phone"
            >
              <Phone className="size-3 text-brand-terracotta" />
              <span className="hidden text-[9px] uppercase tracking-[0.18em] text-slate-400 sm:inline">24hr</span>
              {FIRM.emergencyPhone}
            </a>
            <span className="hidden text-white/20 sm:inline">|</span>
            <div className="hidden items-center gap-3 sm:flex" data-testid="eyebrow-socials">
              <a
                href={FIRM.socials.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Hallinans on Instagram"
                className="text-slate-300 transition-colors hover:text-brand-gold"
                data-testid="eyebrow-social-instagram"
              >
                <Instagram className="size-3.5" />
              </a>
              <a
                href={FIRM.socials.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Hallinans on Facebook"
                className="text-slate-300 transition-colors hover:text-brand-gold"
                data-testid="eyebrow-social-facebook"
              >
                <Facebook className="size-3.5" />
              </a>
              <a
                href={FIRM.socials.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Hallinans on LinkedIn"
                className="text-slate-300 transition-colors hover:text-brand-gold"
                data-testid="eyebrow-social-linkedin"
              >
                <Linkedin className="size-3.5" />
              </a>
            </div>
          </div>
        </div>

        {/* Main bar */}
        <div className={`flex items-center justify-between transition-all duration-300 ${isScrolled ? "h-16" : "h-28"}`}>
          <Link to="/" className="flex items-center" data-testid="nav-logo-link">
            <img
              src="/hallinans-logo.svg"
              alt="Hallinans — Hallinan Blackburn Gittings & Nott"
              className={`w-auto transition-all duration-300 ${isScrolled ? "h-9 sm:h-10" : "h-14 sm:h-[3.7rem]"}`}
              data-testid="nav-logo-img"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center lg:flex" data-testid="desktop-nav">
            <Trigger menu="services" label="Our Services" testId="nav-services-trigger" />
            <Trigger menu="help" label="How We Help" testId="nav-how-we-help-trigger" />
            <Trigger menu="resources" label="Resources" testId="nav-resources-trigger" />
            <NavLink to="/team" className={navLinkCls} data-testid="nav-team-link">
              Team
            </NavLink>
            <NavLink to="/notable-cases" className={navLinkCls} data-testid="nav-notable-cases-link">
              Notable Cases
            </NavLink>
            <NavLink to="/about" className={navLinkCls} data-testid="nav-about-link">
              About
            </NavLink>
            <NavLink to="/contact" className={navLinkCls} data-testid="nav-contact-link">
              Contact
            </NavLink>
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              className={`brand-cta-primary hidden transition-all sm:block ${isScrolled ? "px-4 py-2" : "px-5 py-2.5"}`}
              data-testid="nav-cta-button"
            >
              Get in touch
            </Link>
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="rounded-none p-2 text-white transition-colors hover:bg-white/10 lg:hidden"
              aria-label="Toggle menu"
              data-testid="nav-mobile-toggle"
            >
              {mobileOpen ? <X className="size-5" /> : <Menu className="size-5" />}
            </button>
          </div>
        </div>
        </div>

        {/* Services mega menu — grouped by parent */}
        {openMenu === "services" && (
          <Panel testId="services-mega-menu" onEnter={() => open("services")} onLeave={scheduleClose}>
            <div className="grid grid-cols-12 gap-6">
              {columnGroups.map((g) => {
                const Icon = ICONS[g.icon] || Scale;
                return (
                  <div key={g.slug} className="col-span-3">
                    <Link
                      to={`/services/${g.slug}`}
                      className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-navy transition-colors hover:text-brand-terracotta"
                      data-testid={`mega-group-${g.slug}`}
                    >
                      <Icon className="size-4 text-brand-gold" />
                      {g.title}
                    </Link>
                    <ul className="mt-3 space-y-0.5 border-l border-slate-200 pl-3">
                      {g.children.map((c) => (
                        <li key={c.slug}>
                          <Link
                            to={`/services/${g.slug}/${c.slug}`}
                            className="block rounded px-2 py-1.5 text-sm text-slate-600 transition-colors hover:bg-slate-50 hover:text-brand-navy"
                            data-testid={`mega-offence-${c.slug}`}
                          >
                            {c.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
              <div className="col-span-3 border-l border-slate-200 pl-6">
                {standaloneGroups.map((g) => {
                  const Icon = ICONS[g.icon] || Scale;
                  return (
                    <Link
                      key={g.slug}
                      to={`/services/${g.slug}`}
                      className="group mb-3 flex flex-col rounded-md border border-slate-200 bg-brand-ivory p-4 transition-all duration-200 hover:border-brand-gold/60 hover:shadow-md"
                      data-testid={`mega-group-${g.slug}`}
                    >
                      <span className="flex items-center gap-2 text-sm font-semibold text-brand-navy">
                        <Icon className="size-4 text-brand-gold" /> {g.title}
                      </span>
                      <span className="mt-1 line-clamp-2 text-xs leading-relaxed text-slate-500">
                        {g.tagline}
                      </span>
                    </Link>
                  );
                })}
                <Link
                  to="/services"
                  className="brand-text-link flex items-center gap-2 px-1 pt-1"
                  data-testid="mega-view-all-services"
                >
                  View all services <ArrowRight className="size-3.5" />
                </Link>
              </div>
            </div>
          </Panel>
        )}

        {/* How We Help menu — situations */}
        {openMenu === "help" && (
          <Panel testId="help-mega-menu" onEnter={() => open("help")} onLeave={scheduleClose}>
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-8">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                  Start with your situation
                </p>
                <ul className="mt-3 grid grid-cols-2 gap-x-6 gap-y-0.5">
                  {SITUATIONS.map((s) => {
                    const Icon = ICONS[s.icon] || Scale;
                    return (
                      <li key={s.slug}>
                        <Link
                          to={`/how-we-help/${s.slug}`}
                          className="flex items-center gap-2.5 rounded px-2 py-2 text-sm text-slate-600 transition-colors hover:bg-slate-50 hover:text-brand-navy"
                          data-testid={`mega-situation-${s.slug}`}
                        >
                          <Icon className={`size-4 ${s.urgent ? "text-brand-terracotta" : "text-brand-gold"}`} />
                          {s.title}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
                <Link
                  to="/how-we-help"
                  className="brand-text-link mt-3 flex items-center gap-2 px-2"
                  data-testid="mega-view-all-situations"
                >
                  All situations <ArrowRight className="size-3.5" />
                </Link>
              </div>
              <div className="col-span-4 border-l border-slate-200 pl-6">
                <div className="rounded-md bg-brand-navy p-5 text-white">
                  <p className="text-sm font-semibold">Need help right now?</p>
                  <p className="mt-1.5 text-xs leading-relaxed text-slate-300">
                    Our emergency line is answered 24 hours a day, every day.
                  </p>
                  <a
                    href={`tel:${FIRM.emergencyPhone.replace(/\s/g, "")}`}
                    className="brand-cta-primary mt-4 flex items-center gap-2 px-4 py-2.5 tracking-[0.15em]"
                    data-testid="mega-urgent-call"
                  >
                    <Phone className="size-3.5" /> {FIRM.emergencyPhone}
                  </a>
                </div>
              </div>
            </div>
          </Panel>
        )}

        {/* Resources mega menu */}
        {openMenu === "resources" && (
          <Panel testId="resources-mega-menu" onEnter={() => open("resources")} onLeave={scheduleClose}>
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-8 grid grid-cols-4 gap-4">
                {CATEGORIES.map((c) => {
                  const Icon = ICONS[c.icon] || FileText;
                  return (
                    <Link
                      key={c.slug}
                      to={`/resources/${c.slug}`}
                      className="group flex flex-col rounded-md border border-slate-200 bg-brand-ivory p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-brand-gold/60 hover:shadow-lg"
                      data-testid={`mega-resource-${c.slug}`}
                    >
                      <Icon className="size-6 text-brand-gold" />
                      <span className="mt-8 text-sm font-semibold text-brand-navy">{c.title}</span>
                      <span className="mt-1.5 line-clamp-3 text-xs leading-relaxed text-slate-500">
                        {c.description}
                      </span>
                    </Link>
                  );
                })}
              </div>
              <div className="col-span-4 border-l border-slate-200 pl-6">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                  Latest insights
                </p>
                <ul className="mt-3 space-y-1">
                  {LATEST.map((a) => (
                    <li key={a.slug}>
                      <Link
                        to={`/resources/${a.category}/${a.slug}`}
                        className="block rounded-md px-2 py-2 text-sm leading-snug text-slate-600 transition-colors hover:bg-slate-50 hover:text-brand-navy"
                        data-testid={`mega-latest-${a.slug}`}
                      >
                        {a.title}
                      </Link>
                    </li>
                  ))}
                  <li className="pt-2">
                    <Link
                      to="/resources"
                      className="brand-text-link flex items-center gap-2 px-2"
                      data-testid="mega-view-all-resources"
                    >
                      All legal resources <ArrowRight className="size-3.5" />
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </Panel>
        )}

        {/* Mobile menu */}
        {mobileOpen && (
          <div
            className="absolute inset-x-0 top-full mt-2 max-h-[70vh] overflow-y-auto border border-slate-200 bg-white p-5 shadow-2xl lg:hidden"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col">
              <button
                onClick={() => setMobileSection(mobileSection === "services" ? null : "services")}
                className="flex items-center justify-between border-b border-slate-100 py-3 text-sm font-medium text-brand-navy"
                data-testid="mobile-nav-services-toggle"
              >
                Our Services
                <ChevronDown className={`size-4 transition-transform ${mobileSection === "services" ? "rotate-180" : ""}`} />
              </button>
              {mobileSection === "services" && (
                <div className="border-b border-slate-100 py-2 pl-3">
                  {SERVICE_GROUPS.map((g) => (
                    <div key={g.slug}>
                      <Link
                        to={`/services/${g.slug}`}
                        className="block py-2 text-sm font-semibold text-brand-navy"
                        data-testid={`mobile-nav-group-${g.slug}`}
                      >
                        {g.title}
                      </Link>
                      {g.children.map((c) => (
                        <Link
                          key={c.slug}
                          to={`/services/${g.slug}/${c.slug}`}
                          className="block py-1.5 pl-4 text-sm text-slate-600"
                          data-testid={`mobile-nav-offence-${c.slug}`}
                        >
                          {c.title}
                        </Link>
                      ))}
                    </div>
                  ))}
                  <Link to="/services" className="block py-2 text-xs font-semibold uppercase tracking-widest text-brand-terracotta" data-testid="mobile-nav-all-services">
                    View all services
                  </Link>
                </div>
              )}
              <button
                onClick={() => setMobileSection(mobileSection === "help" ? null : "help")}
                className="flex items-center justify-between border-b border-slate-100 py-3 text-sm font-medium text-brand-navy"
                data-testid="mobile-nav-help-toggle"
              >
                How We Help
                <ChevronDown className={`size-4 transition-transform ${mobileSection === "help" ? "rotate-180" : ""}`} />
              </button>
              {mobileSection === "help" && (
                <div className="border-b border-slate-100 py-2 pl-3">
                  {SITUATIONS.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/how-we-help/${s.slug}`}
                      className="block py-2 text-sm text-slate-600"
                      data-testid={`mobile-nav-situation-${s.slug}`}
                    >
                      {s.title}
                    </Link>
                  ))}
                  <Link to="/how-we-help" className="block py-2 text-xs font-semibold uppercase tracking-widest text-brand-terracotta" data-testid="mobile-nav-all-situations">
                    All situations
                  </Link>
                </div>
              )}
              <button
                onClick={() => setMobileSection(mobileSection === "resources" ? null : "resources")}
                className="flex items-center justify-between border-b border-slate-100 py-3 text-sm font-medium text-brand-navy"
                data-testid="mobile-nav-resources-toggle"
              >
                Resources
                <ChevronDown className={`size-4 transition-transform ${mobileSection === "resources" ? "rotate-180" : ""}`} />
              </button>
              {mobileSection === "resources" && (
                <div className="border-b border-slate-100 py-2 pl-3">
                  {CATEGORIES.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/resources/${c.slug}`}
                      className="block py-2 text-sm text-slate-600"
                      data-testid={`mobile-nav-resource-${c.slug}`}
                    >
                      {c.title}
                    </Link>
                  ))}
                  <Link to="/resources" className="block py-2 text-xs font-semibold uppercase tracking-widest text-brand-terracotta" data-testid="mobile-nav-all-resources">
                    All legal resources
                  </Link>
                </div>
              )}
              <Link to="/team" className="border-b border-slate-100 py-3 text-sm font-medium text-brand-navy" data-testid="mobile-nav-team">
                Team
              </Link>
              <Link to="/notable-cases" className="border-b border-slate-100 py-3 text-sm font-medium text-brand-navy" data-testid="mobile-nav-notable-cases">
                Notable Cases
              </Link>
              <Link to="/about" className="border-b border-slate-100 py-3 text-sm font-medium text-brand-navy" data-testid="mobile-nav-about">
                About
              </Link>
              <Link to="/contact" className="border-b border-slate-100 py-3 text-sm font-medium text-brand-navy" data-testid="mobile-nav-contact">
                Contact
              </Link>
              <a
                href={`tel:${FIRM.phone.replace(/\s/g, "")}`}
                className="flex items-center gap-2 py-3 text-sm font-medium text-brand-terracotta"
                data-testid="mobile-nav-phone"
              >
                <Phone className="size-4" /> Office: {FIRM.phone}
              </a>
              <div className="flex items-center gap-5 border-t border-slate-100 py-3" data-testid="mobile-socials">
                <a href={FIRM.socials.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-slate-500 transition-colors hover:text-brand-gold" data-testid="mobile-social-instagram">
                  <Instagram className="size-5" />
                </a>
                <a href={FIRM.socials.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-slate-500 transition-colors hover:text-brand-gold" data-testid="mobile-social-facebook">
                  <Facebook className="size-5" />
                </a>
                <a href={FIRM.socials.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-slate-500 transition-colors hover:text-brand-gold" data-testid="mobile-social-linkedin">
                  <Linkedin className="size-5" />
                </a>
              </div>
              <Link
                to="/contact"
                className="brand-cta-primary mt-2 px-5 py-3 text-center"
                data-testid="mobile-nav-cta"
              >
                Get in touch
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
